export const ENTRY_PERKS_VERSION = 1;
export const ENTRY_PERK_TYPES = Object.freeze(['Host', 'Head Host', 'Ambassador', 'DJ', 'Management', 'Admin']);
export const ENTRY_PERK_FIELDS = Object.freeze(['freeQuota', 'drinkCards', 'hundredDrinkcard']);
export const ENTRY_PERK_OVERRIDE_TEAMS = Object.freeze(['Yours Truly']);
export const ENTRY_PERK_ALLOWED_OVERRIDE_TYPES = Object.freeze({
  'Yours Truly': Object.freeze(['Host'])
});

const ZERO_PERKS = Object.freeze({ freeQuota: 0, drinkCards: 0, hundredDrinkcard: 0 });

export const CLUB_ASTRA_ENTRY_PERKS = Object.freeze({
  version: ENTRY_PERKS_VERSION,
  defaultsByType: Object.freeze({
    Host: Object.freeze({ freeQuota: 2, drinkCards: 2, hundredDrinkcard: 0 }),
    'Head Host': Object.freeze({ freeQuota: 3, drinkCards: 0, hundredDrinkcard: 1 }),
    Ambassador: Object.freeze({ freeQuota: 4, drinkCards: 0, hundredDrinkcard: 1 }),
    DJ: Object.freeze({ freeQuota: 2, drinkCards: 2, hundredDrinkcard: 0 }),
    Management: Object.freeze({ freeQuota: 0, drinkCards: 0, hundredDrinkcard: 0 }),
    Admin: Object.freeze({ freeQuota: 0, drinkCards: 0, hundredDrinkcard: 0 })
  }),
  teamOverrides: Object.freeze({
    'Yours Truly': Object.freeze({
      Host: Object.freeze({ freeQuota: 0, drinkCards: 0, hundredDrinkcard: 0 })
    })
  })
});

export const LEGACY_ENTRY_PERKS = CLUB_ASTRA_ENTRY_PERKS;
export const PANDA_ENTRY_PERKS = CLUB_ASTRA_ENTRY_PERKS;
export const NEUTRAL_ENTRY_PERKS = CLUB_ASTRA_ENTRY_PERKS;

export function cloneEntryPerks(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value || {}));
}

function cleanString(value) {
  return String(value || '').trim();
}

function normalizeComparable(value) {
  return cleanString(value).toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

function canonicalEntryType(value) {
  const wanted = normalizeComparable(value);
  return ENTRY_PERK_TYPES.find((type) => normalizeComparable(type) === wanted) || 'Host';
}

function canonicalOverrideTeam(value) {
  const wanted = normalizeComparable(value);
  return ENTRY_PERK_OVERRIDE_TEAMS.find((team) => normalizeComparable(team) === wanted) || null;
}

function integerForRead(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.floor(n));
}

function rowForRead(rawRow, fallbackRow = ZERO_PERKS) {
  const row = rawRow && typeof rawRow === 'object' && !Array.isArray(rawRow) ? rawRow : {};
  return {
    freeQuota: integerForRead(row.freeQuota, fallbackRow.freeQuota || 0),
    drinkCards: integerForRead(row.drinkCards ?? row.drinkcards, fallbackRow.drinkCards || 0),
    hundredDrinkcard: integerForRead(row.hundredDrinkcard, fallbackRow.hundredDrinkcard || 0)
  };
}

export function normalizeEntryPerksForRead(raw, fallback = CLUB_ASTRA_ENTRY_PERKS) {
  const fallbackConfig = fallback && typeof fallback === 'object' ? fallback : CLUB_ASTRA_ENTRY_PERKS;
  const input = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const rawDefaults =
    input.defaultsByType && typeof input.defaultsByType === 'object' && !Array.isArray(input.defaultsByType) ? input.defaultsByType : {};
  const fallbackDefaults =
    fallbackConfig.defaultsByType && typeof fallbackConfig.defaultsByType === 'object'
      ? fallbackConfig.defaultsByType
      : CLUB_ASTRA_ENTRY_PERKS.defaultsByType;

  const defaultsByType = {};
  for (const type of ENTRY_PERK_TYPES) {
    defaultsByType[type] = rowForRead(rawDefaults[type], fallbackDefaults[type] || ZERO_PERKS);
  }

  const fallbackOverrides =
    fallbackConfig.teamOverrides && typeof fallbackConfig.teamOverrides === 'object'
      ? fallbackConfig.teamOverrides
      : CLUB_ASTRA_ENTRY_PERKS.teamOverrides;
  const rawOverrides =
    input.teamOverrides && typeof input.teamOverrides === 'object' && !Array.isArray(input.teamOverrides) ? input.teamOverrides : {};
  const teamOverrides = {};
  for (const team of ENTRY_PERK_OVERRIDE_TEAMS) {
    const allowedTypes = ENTRY_PERK_ALLOWED_OVERRIDE_TYPES[team] || [];
    const rawTeam =
      rawOverrides[team] && typeof rawOverrides[team] === 'object' && !Array.isArray(rawOverrides[team]) ? rawOverrides[team] : {};
    const fallbackTeam = fallbackOverrides[team] && typeof fallbackOverrides[team] === 'object' ? fallbackOverrides[team] : {};
    teamOverrides[team] = {};
    for (const type of allowedTypes) {
      teamOverrides[team][type] = rowForRead(rawTeam[type], fallbackTeam[type] || defaultsByType[type] || ZERO_PERKS);
    }
  }

  return {
    version: ENTRY_PERKS_VERSION,
    defaultsByType,
    teamOverrides
  };
}

function intForWrite(value, path) {
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) {
    throw new Error(`${path} must be a non-negative integer`);
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    throw new Error(`${path} must be a non-negative integer`);
  }
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) {
    throw new Error(`${path} must be a non-negative integer`);
  }
  return n;
}

function versionForWrite(value) {
  if (value === undefined) return ENTRY_PERKS_VERSION;
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) {
    throw new Error(`entryPerks.version must be ${ENTRY_PERKS_VERSION}`);
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    throw new Error(`entryPerks.version must be ${ENTRY_PERKS_VERSION}`);
  }
  const n = Number(value);
  if (!Number.isInteger(n) || n !== ENTRY_PERKS_VERSION) {
    throw new Error(`entryPerks.version must be ${ENTRY_PERKS_VERSION}`);
  }
  return n;
}

function rejectUnknownKeys(obj, allowedKeys, path) {
  for (const key of Object.keys(obj || {})) {
    if (!allowedKeys.includes(key)) throw new Error(`${path}.${key} is not supported`);
  }
}

function rowForWrite(row, path) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    throw new Error(`${path} must be an object`);
  }
  rejectUnknownKeys(row, ENTRY_PERK_FIELDS, path);
  const out = {};
  for (const field of ENTRY_PERK_FIELDS) {
    if (row[field] === undefined || row[field] === '') {
      throw new Error(`${path}.${field} is required`);
    }
    out[field] = intForWrite(row[field], `${path}.${field}`);
  }
  return out;
}

export function validateEntryPerksForWrite(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('entryPerks must be an object');
  }
  rejectUnknownKeys(raw, ['version', 'defaultsByType', 'teamOverrides'], 'entryPerks');
  versionForWrite(raw.version);
  if (!raw.defaultsByType || typeof raw.defaultsByType !== 'object' || Array.isArray(raw.defaultsByType)) {
    throw new Error('entryPerks.defaultsByType must be an object');
  }
  rejectUnknownKeys(raw.defaultsByType, ENTRY_PERK_TYPES, 'entryPerks.defaultsByType');

  const defaultsByType = {};
  for (const type of ENTRY_PERK_TYPES) {
    defaultsByType[type] = rowForWrite(raw.defaultsByType[type], `entryPerks.defaultsByType.${type}`);
  }

  if (!raw.teamOverrides || typeof raw.teamOverrides !== 'object' || Array.isArray(raw.teamOverrides)) {
    throw new Error('entryPerks.teamOverrides must be an object');
  }
  rejectUnknownKeys(raw.teamOverrides, ENTRY_PERK_OVERRIDE_TEAMS, 'entryPerks.teamOverrides');

  const teamOverrides = {};
  for (const team of ENTRY_PERK_OVERRIDE_TEAMS) {
    const allowedTypes = ENTRY_PERK_ALLOWED_OVERRIDE_TYPES[team] || [];
    const teamInput = raw.teamOverrides[team];
    if (!teamInput || typeof teamInput !== 'object' || Array.isArray(teamInput)) {
      throw new Error(`entryPerks.teamOverrides.${team} must be an object`);
    }
    rejectUnknownKeys(teamInput, allowedTypes, `entryPerks.teamOverrides.${team}`);
    teamOverrides[team] = {};
    for (const type of allowedTypes) {
      teamOverrides[team][type] = rowForWrite(teamInput[type], `entryPerks.teamOverrides.${team}.${type}`);
    }
  }

  return {
    version: ENTRY_PERKS_VERSION,
    defaultsByType,
    teamOverrides
  };
}

export function resolvePerksForType(config, type, options = {}) {
  const normalized = normalizeEntryPerksForRead(config);
  const entryType = canonicalEntryType(type);
  const overrideTeam = canonicalOverrideTeam(options.team);
  if (overrideTeam && normalized.teamOverrides?.[overrideTeam]?.[entryType]) {
    return { ...normalized.teamOverrides[overrideTeam][entryType] };
  }
  return { ...(normalized.defaultsByType[entryType] || normalized.defaultsByType.Host) };
}

export function getDefaultEntryPerksForEvent(eventId) {
  const id = cleanString(eventId).toLowerCase();
  if (id === 'club-astra') return cloneEntryPerks(CLUB_ASTRA_ENTRY_PERKS);
  if (id === 'panda-thursdays') return cloneEntryPerks(PANDA_ENTRY_PERKS);
  return cloneEntryPerks(NEUTRAL_ENTRY_PERKS);
}
